// This file contains a catalog of keywords for each case study
// These keywords are used for search functionality to help users find relevant content
// Define types for our keyword catalog
export interface CaseStudyKeyword {
  keyword: string;
  weight: number; // 1-10 scale to indicate importance
  category: 'skill' | 'function' | 'goal' | 'industry' | 'technology' | 'concept';
}
export interface CaseStudyData {
  slug: string;
  title: string;
  description: string;
  image: string;
  keywords: CaseStudyKeyword[];
  public: boolean; // Add public flag for content gating
}
// Comprehensive keyword catalog for each case study
export const caseStudyCatalog: CaseStudyData[] = [{
  slug: 'ai-comparison',
  title: 'Watson vs Dialogflow / Project EVE',
  description: 'Conversational design proof of concept that we built using IBM Watson and Google Dialogflow for a large financial firm looking to elevate simple tasks at its call centers.',
  image: "/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png",
  public: false,
  // Hidden for general viewers (NDA/sensitive)
  keywords: [
  // Skills
  {
    keyword: 'conversational design',
    weight: 10,
    category: 'skill'
  }, {
    keyword: 'ai strategy',
    weight: 10,
    category: 'skill'
  }, {
    keyword: 'platform evaluation',
    weight: 9,
    category: 'skill'
  }, {
    keyword: 'technical communication',
    weight: 8,
    category: 'skill'
  }, {
    keyword: 'user experience',
    weight: 7,
    category: 'skill'
  }, {
    keyword: 'dialogue patterns',
    weight: 8,
    category: 'skill'
  }, {
    keyword: 'conversation flow',
    weight: 7,
    category: 'skill'
  },
  // Functions
  {
    keyword: 'track status',
    weight: 7,
    category: 'function'
  }, {
    keyword: 'fee lookup',
    weight: 7,
    category: 'function'
  }, {
    keyword: 'chatbot',
    weight: 8,
    category: 'function'
  }, {
    keyword: 'call center automation',
    weight: 9,
    category: 'function'
  }, {
    keyword: 'customer service',
    weight: 8,
    category: 'function'
  },
  // Goals
  {
    keyword: 'reduce call volume',
    weight: 9,
    category: 'goal'
  }, {
    keyword: 'improve customer experience',
    weight: 8,
    category: 'goal'
  }, {
    keyword: 'automate interactions',
    weight: 8,
    category: 'goal'
  }, {
    keyword: 'cost reduction',
    weight: 7,
    category: 'goal'
  }, {
    keyword: 'efficiency',
    weight: 7,
    category: 'goal'
  },
  // Industry
  {
    keyword: 'financial services',
    weight: 6,
    category: 'industry'
  }, {
    keyword: 'moneygram',
    weight: 10,
    category: 'industry'
  }, {
    keyword: 'money transfer',
    weight: 6,
    category: 'industry'
  },
  // Technology
  {
    keyword: 'ibm watson',
    weight: 10,
    category: 'technology'
  }, {
    keyword: 'google dialogflow',
    weight: 10,
    category: 'technology'
  }, {
    keyword: 'ai',
    weight: 9,
    category: 'technology'
  }, {
    keyword: 'artificial intelligence',
    weight: 9,
    category: 'technology'
  }, {
    keyword: 'natural language processing',
    weight: 8,
    category: 'technology'
  }, {
    keyword: 'nlp',
    weight: 8,
    category: 'technology'
  }, {
    keyword: 'natural language understanding',
    weight: 8,
    category: 'technology'
  }, {
    keyword: 'nlu',
    weight: 8,
    category: 'technology'
  }, {
    keyword: 'speech to text',
    weight: 7,
    category: 'technology'
  }, {
    keyword: 'text to speech',
    weight: 7,
    category: 'technology'
  }, {
    keyword: 'conversational ai',
    weight: 10,
    category: 'technology'
  },
  // Concepts
  {
    keyword: 'intents',
    weight: 7,
    category: 'concept'
  }, {
    keyword: 'entities',
    weight: 7,
    category: 'concept'
  }, {
    keyword: 'slot filling',
    weight: 6,
    category: 'concept'
  }, {
    keyword: 'project eve',
    weight: 10,
    category: 'concept'
  }, {
    keyword: 'case western',
    weight: 9,
    category: 'concept'
  }, {
    keyword: 'featured',
    weight: 7,
    category: 'concept'
  }]
}, {
  slug: 'cyber-security',
  title: 'Unisys Stealth / Cyber Security',
  description: 'My first test as a designer on a large platform re-design for a cybersecurity project. I helped transform complex micro segmentation and threat analysis applications into intuitive, user-centered solutions.',
  image: "/unisys_thumb.png",
  public: false,
  // Hidden for general viewers (NDA/sensitive)
  keywords: [
  // Skills
  {
    keyword: 'ux design',
    weight: 9,
    category: 'skill'
  }, {
    keyword: 'ui design',
    weight: 9,
    category: 'skill'
  }, {
    keyword: 'technical communication',
    weight: 8,
    category: 'skill'
  }, {
    keyword: 'complex system simplification',
    weight: 10,
    category: 'skill'
  }, {
    keyword: 'information architecture',
    weight: 7,
    category: 'skill'
  }, {
    keyword: 'visual design',
    weight: 7,
    category: 'skill'
  }, {
    keyword: 'user research',
    weight: 8,
    category: 'skill'
  },
  // Functions
  {
    keyword: 'threat analysis',
    weight: 9,
    category: 'function'
  }, {
    keyword: 'micro segmentation',
    weight: 10,
    category: 'function'
  }, {
    keyword: 'security visualization',
    weight: 8,
    category: 'function'
  }, {
    keyword: 'network monitoring',
    weight: 7,
    category: 'function'
  }, {
    keyword: 'security dashboard',
    weight: 8,
    category: 'function'
  },
  // Goals
  {
    keyword: 'improve usability',
    weight: 9,
    category: 'goal'
  }, {
    keyword: 'sales impact',
    weight: 8,
    category: 'goal'
  }, {
    keyword: 'simplify complexity',
    weight: 10,
    category: 'goal'
  }, {
    keyword: 'competitive redesign',
    weight: 8,
    category: 'goal'
  }, {
    keyword: 'technical balance',
    weight: 7,
    category: 'goal'
  },
  // Industry
  {
    keyword: 'cybersecurity',
    weight: 10,
    category: 'industry'
  }, {
    keyword: 'security',
    weight: 10,
    category: 'industry'
  }, {
    keyword: 'unisys',
    weight: 10,
    category: 'industry'
  }, {
    keyword: 'enterprise',
    weight: 6,
    category: 'industry'
  },
  // Technology
  {
    keyword: 'stealth',
    weight: 10,
    category: 'technology'
  }, {
    keyword: 'stealth aware',
    weight: 9,
    category: 'technology'
  }, {
    keyword: 'network security',
    weight: 8,
    category: 'technology'
  }, {
    keyword: 'security platform',
    weight: 8,
    category: 'technology'
  },
  // Concepts
  {
    keyword: 'protection',
    weight: 7,
    category: 'concept'
  }, {
    keyword: 'digital protection',
    weight: 8,
    category: 'concept'
  }, {
    keyword: 'cyber',
    weight: 10,
    category: 'concept'
  }, {
    keyword: 'network visualization',
    weight: 7,
    category: 'concept'
  }, {
    keyword: 'security policies',
    weight: 6,
    category: 'concept'
  }]
}, {
  slug: 'us-bank',
  title: 'US Bank Commercial Card',
  description: 'A look at how I improved customer satisfaction and team productivity on a Corporate Payment Services team focused on the One Card application at U.S. Bank.',
  image: "/usbthumb.png",
  public: true,
  // Visible to all viewers
  keywords: [
  // Skills
  {
    keyword: 'ux design',
    weight: 9,
    category: 'skill'
  }, {
    keyword: 'ui design',
    weight: 8,
    category: 'skill'
  }, {
    keyword: 'user research',
    weight: 7,
    category: 'skill'
  }, {
    keyword: 'wireframing',
    weight: 6,
    category: 'skill'
  }, {
    keyword: 'prototyping',
    weight: 6,
    category: 'skill'
  }, {
    keyword: 'usability testing',
    weight: 7,
    category: 'skill'
  },
  // Functions
  {
    keyword: 'payment processing',
    weight: 8,
    category: 'function'
  }, {
    keyword: 'corporate card management',
    weight: 9,
    category: 'function'
  }, {
    keyword: 'financial services',
    weight: 10,
    category: 'function'
  }, {
    keyword: 'customer support',
    weight: 7,
    category: 'function'
  },
  // Goals
  {
    keyword: 'improve customer satisfaction',
    weight: 10,
    category: 'goal'
  }, {
    keyword: 'increase team productivity',
    weight: 9,
    category: 'goal'
  }, {
    keyword: 'streamline processes',
    weight: 8,
    category: 'goal'
  }, {
    keyword: 'enhance user experience',
    weight: 8,
    category: 'goal'
  },
  // Industry
  {
    keyword: 'banking',
    weight: 10,
    category: 'industry'
  }, {
    keyword: 'financial',
    weight: 10,
    category: 'industry'
  }, {
    keyword: 'us bank',
    weight: 10,
    category: 'industry'
  }, {
    keyword: 'corporate banking',
    weight: 8,
    category: 'industry'
  }, {
    keyword: 'financial services',
    weight: 9,
    category: 'industry'
  },
  // Technology
  {
    keyword: 'one card',
    weight: 10,
    category: 'technology'
  }, {
    keyword: 'payment system',
    weight: 8,
    category: 'technology'
  }, {
    keyword: 'banking platform',
    weight: 7,
    category: 'technology'
  },
  // Concepts
  {
    keyword: 'corporate payment services',
    weight: 9,
    category: 'concept'
  }, {
    keyword: 'commercial card',
    weight: 9,
    category: 'concept'
  }, {
    keyword: 'financial',
    weight: 8,
    category: 'concept'
  }, {
    keyword: 'banking',
    weight: 8,
    category: 'concept'
  }]
}];
// List of common domain areas that aren't in the portfolio
// This helps identify when users are searching for work that doesn't exist
const nonMatchingDomains = [
// Architecture and construction
'construction', 'architecture', 'building', 'architect', 'renovation', 'interior design',
// Landscaping and outdoors
'landscape', 'gardening', 'agriculture', 'farming', 'outdoor', 'yard', 'lawn',
// Retail and commerce
'retail', 'e-commerce', 'shop', 'store', 'shopping', 'commerce', 'marketplace',
// Healthcare
'healthcare', 'medical', 'hospital', 'clinic', 'doctor', 'patient', 'health',
// Education
'education', 'learning', 'school', 'university', 'teaching', 'student', 'academic',
// Entertainment
'gaming', 'game', 'video game', 'mobile game', 'entertainment', 'streaming',
// Real estate
'real estate', 'property', 'housing', 'home', 'apartment', 'rental',
// Transportation
'transportation', 'automotive', 'car', 'vehicle', 'transit', 'travel', 'aviation', 'airline', 'flight', 'airplane', 'aircraft',
// Food and hospitality
'hospitality', 'hotel', 'restaurant', 'food', 'catering', 'culinary',
// Manufacturing and industrial
'manufacturing', 'factory', 'industrial', 'production', 'machinery',
// Media and entertainment
'media', 'entertainment', 'streaming', 'video', 'film', 'movie', 'television',
// Legal
'legal', 'law', 'attorney', 'lawyer', 'court', 'judicial',
// Additional domains to catch more non-matching searches
'fashion', 'clothing', 'apparel', 'sports', 'fitness', 'gym', 'social media', 'insurance', 'logistics', 'supply chain', 'shipping', 'delivery', 'nonprofit', 'charity', 'environmental', 'sustainability', 'energy', 'utilities', 'telecommunications', 'telecom', 'pharmaceutical', 'pharma', 'drug', 'medicine'];
/**
 * Search for case studies based on a search term
 * @param searchTerm The search term to match against case study keywords
 * @returns An array of case study slugs sorted by relevance score
 */
export function searchCaseStudies(searchTerm: string): {
  slug: string;
  score: number;
}[] {
  const normalizedSearchTerm = searchTerm.toLowerCase().trim();
  // If the search term is empty, return all case studies
  if (!normalizedSearchTerm) {
    return caseStudyCatalog.map(study => ({
      slug: study.slug,
      score: 1
    }));
  }
  console.log(`Searching for: "${normalizedSearchTerm}"`);
  // Break the search term into individual words
  const searchWords = normalizedSearchTerm.split(/\s+/);
  // First, check if there are domain-specific terms that should trigger no match
  // This is a more aggressive check to catch searches for domains not in the portfolio
  for (const domain of nonMatchingDomains) {
    // Check if the domain is in the search term
    if (normalizedSearchTerm.includes(domain)) {
      console.log(`Found non-matching domain: "${domain}" in search term`);
      return []; // Return empty array to trigger NoMatchModal
    }
    // Check individual words in the search for domain matches
    for (const word of searchWords) {
      if (word.length > 3) {
        // Only consider meaningful words (not "of", "the", etc)
        if (word === domain || domain === word) {
          console.log(`Found exact non-matching domain word: "${word}"`);
          return []; // Return empty array to trigger NoMatchModal
        }
        // Check for partial matches that are significant
        if ((domain.includes(word) || word.includes(domain)) && (word.length > 4 || domain.length > 4)) {
          console.log(`Found partial non-matching domain: "${word}" related to "${domain}"`);
          return []; // Return empty array to trigger NoMatchModal
        }
      }
    }
  }
  // Look for "case study" or "project" combined with unknown industry/domain terms
  if (normalizedSearchTerm.includes('case study') || normalizedSearchTerm.includes('project') || normalizedSearchTerm.includes('portfolio') || normalizedSearchTerm.includes('work')) {
    // Extract other words that might be domain indicators
    const potentialDomains = searchWords.filter(word => word !== 'case' && word !== 'study' && word !== 'project' && word !== 'portfolio' && word !== 'work' && word.length > 3);
    // Check if these potential domains match any of our case studies
    if (potentialDomains.length > 0) {
      let foundMatch = false;
      for (const domain of potentialDomains) {
        // Check if this domain appears in any of our case study keywords
        const domainMatchesPortfolio = caseStudyCatalog.some(study => study.keywords.some(keyword => keyword.keyword.includes(domain) || domain.includes(keyword.keyword)));
        if (!domainMatchesPortfolio) {
          console.log(`Found potential unknown domain "${domain}" with case study/project term`);
          return []; // Return empty array to trigger NoMatchModal
        } else {
          foundMatch = true;
        }
      }
      // If we found matches for all potential domains, continue with normal search
      if (foundMatch) {
        console.log('All potential domains match our portfolio, continuing with search');
      }
    }
  }
  // Calculate a score for each case study based on keyword matches
  const results = caseStudyCatalog.map(study => {
    let score = 0;
    let matchedKeywords = new Set(); // Track which keywords matched to avoid double counting
    // First check for full phrase match across all keywords
    study.keywords.forEach(keywordObj => {
      // Full phrase match gets highest priority
      if (keywordObj.keyword.includes(normalizedSearchTerm) || normalizedSearchTerm.includes(keywordObj.keyword)) {
        // If the keyword is an industry or concept, give it higher weight
        const categoryMultiplier = keywordObj.category === 'industry' ? 2.0 : keywordObj.category === 'concept' ? 1.8 : 1.5;
        const matchScore = keywordObj.weight * (keywordObj.keyword === normalizedSearchTerm ? 2 * categoryMultiplier : categoryMultiplier);
        score += matchScore;
        matchedKeywords.add(keywordObj.keyword);
      }
    });
    // Then check individual word matches
    searchWords.forEach(word => {
      if (word.length <= 2) return; // Skip very short words like "of", "to", etc.
      study.keywords.forEach(keywordObj => {
        if (matchedKeywords.has(keywordObj.keyword)) return; // Skip already matched keywords
        // Split keyword into individual words
        const keywordWords = keywordObj.keyword.split(/\s+/);
        // Exact match gets full weight
        if (keywordObj.keyword === word) {
          // Industry and concept keywords get higher weight
          const categoryMultiplier = keywordObj.category === 'industry' ? 1.5 : keywordObj.category === 'concept' ? 1.3 : 1.0;
          score += keywordObj.weight * categoryMultiplier;
          matchedKeywords.add(keywordObj.keyword);
        }
        // Check if the word exactly matches one of the words in the keyword
        else if (keywordWords.includes(word)) {
          // Industry and concept keywords get higher weight
          const categoryMultiplier = keywordObj.category === 'industry' ? 1.3 : keywordObj.category === 'concept' ? 1.2 : 1.0;
          score += keywordObj.weight * 0.7 * categoryMultiplier; // Partial weight for matching a whole word in a phrase
          matchedKeywords.add(keywordObj.keyword);
        }
        // Check for semantic similarity - words that are related but not exact matches
        else {
          // Check if the word is semantically related to this keyword
          const semanticMatch = isSemanticMatch(word, keywordObj.keyword);
          if (semanticMatch) {
            score += keywordObj.weight * 0.5; // Lower weight for semantic matches
            matchedKeywords.add(keywordObj.keyword);
          }
        }
      });
    });
    return {
      slug: study.slug,
      score
    };
  });
  // Log the results for debugging
  console.log('Search results:', results.map(r => `${r.slug}: ${r.score}`).join(', '));
  // Sort by score (highest first) and filter out zero scores
  // Only return results with a minimum score to prevent weak matches
  return results.filter(result => result.score > 5) // Increased minimum score threshold
  .sort((a, b) => b.score - a.score);
}
/**
 * Check if two terms are semantically related
 * This is a simple implementation that could be expanded with a more sophisticated approach
 */
function isSemanticMatch(searchWord: string, keyword: string): boolean {
  // Define some semantic relationships
  const semanticGroups = [
  // UX/UI related terms
  ['ux', 'ui', 'user experience', 'user interface', 'design', 'usability'],
  // Security related terms
  ['security', 'protection', 'cyber', 'cybersecurity', 'safety', 'defense'],
  // Financial related terms
  ['financial', 'banking', 'payment', 'money', 'transaction', 'economic'],
  // AI related terms
  ['ai', 'artificial intelligence', 'machine learning', 'ml', 'chatbot', 'bot'],
  // Work/project related terms
  ['work', 'project', 'case study', 'portfolio', 'experience']];
  // Check if the search word and keyword are in the same semantic group
  for (const group of semanticGroups) {
    if (group.some(term => term.includes(searchWord) || searchWord.includes(term)) && group.some(term => term.includes(keyword) || keyword.includes(term))) {
      return true;
    }
  }
  return false;
}
/**
 * Get the top matching case study for a search term
 * @param searchTerm The search term to match
 * @returns The slug of the best matching case study, or null if no matches
 */
export function getTopMatchingCaseStudy(searchTerm: string): string | null {
  const matches = searchCaseStudies(searchTerm);
  return matches.length > 0 ? matches[0].slug : null;
}
/**
 * Get visible case studies based on user type
 * @param userType The type of user viewing the portfolio
 * @returns Filtered array of case studies
 */
export function getVisibleCaseStudies(userType: 'hiring_manager' | 'recruiter' | 'general' | null) {
  if (userType === 'general') {
    return caseStudyCatalog.filter(cs => cs.public);
  }
  return caseStudyCatalog; // All visible for hiring_manager and recruiter
}